import {DealerData, DealerRequest, useCompanies, useCreateDealer, useDealer, useUpdateDealer} from '@api'
import {Country, FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  FormFieldItem,
  generateReadableErrorMessages,
  getUserData,
  phoneMasks,
  useViewNavigate,
  validateFields,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

/**
 * Initial data
 */
const initialDealerData: DealerData = {
  name: '',
  customerNo: '',
  email: '',
  address: '',
  city: '',
  companyNo: '',
  dealerNo: '',
  officialSignatories: [
    {
      dateOfBirth: '',
      email: '',
      name: '',
      personalNumber: '',
    },
  ],
  phone: '',
  vatNumber: '',
  invoiceEmail: '',
  invoicePhone: '',
  zipCode: '',
  companyId: null,
  active: true,
  allowedContractTypes: [],
}

export function useDealerFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [dealerData, setDealerData] = useState<DealerData>(initialDealerData)
  const [dealerFormFieldsErrors, setDealerFormFieldsErrors] = useState<string[]>([])
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()
  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data: dealerResponse} = useDealer(id)
  const {data: companiesData} = useCompanies()
  const {mutate: updateDealerMutation} = useUpdateDealer()
  const {mutate: createDealerMutation} = useCreateDealer()

  useEffect(() => {
    if (id && dealerResponse) {
      setDealerData({
        active: dealerResponse.active,
        address: dealerResponse.address,
        companyId: dealerResponse.companyId,
        customerNo: dealerResponse.customerNo,
        email: dealerResponse.email,
        name: dealerResponse.name,
        id: dealerResponse.id,
        city: dealerResponse.city,
        companyNo: dealerResponse.companyNo,
        dealerNo: dealerResponse.dealerNo,
        officialSignatories: dealerResponse.officialSignatories || [],
        allowedContractTypes: dealerResponse.allowedContractTypes,
        phone: dealerResponse.phone,
        invoiceEmail: dealerResponse.invoiceEmail,
        invoicePhone: dealerResponse.invoicePhone,
        vatNumber: dealerResponse.vatNumber,
        zipCode: dealerResponse.zipCode,
      })
    } else {
      setDealerData(prev => ({...prev, companyId: userSelection?.companyId || null}))
    }
  }, [dealerResponse, id])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Dealer search']),
          link: NavigationPathKey.DEALER_SEARCH,
        },
        {title: id ? t(TranslationKey['Edit dealer']) : t(TranslationKey['New dealer'])},
      ]),
    [id],
  )

  /**
   * Countries that require a personal number for official signatory
   */
  const personalNumberCountries = [Country.SWEDEN, Country.NORWAY, Country.FINLAND, Country.DENMARK]

  /**
   * A function handling Dealer form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form fields
   */
  const onChangeDealerFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = checked ?? event.target.value

    setDealerFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setDealerData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Dynamically generated phone input mask
   */
  const phoneMask: string = useMemo(() => {
    const country = companiesData?.find(company => company.id === userSelection.companyId)?.country

    if (!country) return '###############'
    return phoneMasks[country]
  }, [userSelection, companiesData])

  /**
   * A function handling Official signatory form fields change
   * @param index Official signatory section index
   * @param event Change event
   * @param date Date value
   * @param overrideFieldName Field name
]   */
  const onChangeOfficialSignatoryFormField = (
    index: number,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    date?: string | null,
    overrideFieldName?: string,
  ) => {
    setIsFormDirty(true)
    const fieldName = overrideFieldName ?? event?.target.name
    const value = date ?? event?.target.value

    if (!fieldName || value === undefined) return

    setDealerFormFieldsErrors(prev => prev.filter(item => item !== `officialSignatories[${index}].${fieldName}`))
    setDealerData(prev => ({
      ...prev,
      officialSignatories: prev.officialSignatories.map((officialSignatory, i) => {
        return i === index ? {...officialSignatory, [fieldName]: value} : officialSignatory
      }),
    }))
  }

  /**
   * Dealer form fields configuration - general section
   */
  const dealerFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Dealer name']),
      name: 'name',
      onChange: onChangeDealerFormField,
      value: dealerData.name,
      error: dealerFormFieldsErrors.includes('name'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Dealer number']),
      name: 'dealerNo',
      onChange: onChangeDealerFormField,
      value: dealerData.dealerNo,
      error: dealerFormFieldsErrors.includes('dealerNo'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['VAT number']),
      name: 'vatNumber',
      onChange: onChangeDealerFormField,
      value: dealerData.vatNumber,
      error: dealerFormFieldsErrors.includes('vatNumber'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Company number']),
      name: 'companyNo',
      onChange: onChangeDealerFormField,
      value: dealerData.companyNo,
      error: dealerFormFieldsErrors.includes('companyNo'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Customer number']),
      name: 'customerNo',
      onChange: onChangeDealerFormField,
      value: dealerData.customerNo,
      error: dealerFormFieldsErrors.includes('customerNo'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Email),
      name: 'email',
      onChange: onChangeDealerFormField,
      value: dealerData.email,
      error: dealerFormFieldsErrors.includes('email'),
    },
    {
      fieldType: FormFieldType.PHONE_INPUT,
      label: t(TranslationKey.Phone),
      name: 'phone',
      mask: phoneMask,
      onChange: onChangeDealerFormField,
      value: dealerData.phone,
      error: dealerFormFieldsErrors.includes('phone'),
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      name: 'active',
      label: t(TranslationKey.Active),
      checkboxes: [
        {
          checked: dealerData.active,
          label: t(TranslationKey.Active),
          value: dealerData.active,
          name: 'active',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeDealerFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * Dealer form fields configuration - contact section
   */
  const dealerContactFormFields = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Invoice email']),
      name: 'invoiceEmail',
      onChange: onChangeDealerFormField,
      value: dealerData.invoiceEmail,
      error: dealerFormFieldsErrors.includes('invoicePhone'),
    },
    {
      fieldType: FormFieldType.PHONE_INPUT,
      label: t(TranslationKey['Invoice phone']),
      name: 'invoicePhone',
      mask: phoneMask,
      onChange: onChangeDealerFormField,
      value: dealerData.invoicePhone,
      error: dealerFormFieldsErrors.includes('invoiceEmail'),
    },
    {} as FormFieldItem,
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Address),
      name: 'address',
      onChange: onChangeDealerFormField,
      value: dealerData.address,
      error: dealerFormFieldsErrors.includes('address'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Zip code']),
      name: 'zipCode',
      onChange: onChangeDealerFormField,
      value: dealerData.zipCode,
      error: dealerFormFieldsErrors.includes('zipCode'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.City),
      name: 'city',
      onChange: onChangeDealerFormField,
      value: dealerData.city,
      error: dealerFormFieldsErrors.includes('city'),
    },
  ]

  /**
   * Official signatory form fields configuration
   * @param index Official signatory section index
   * @returns Official signatory form fields
   */
  const dealerOfficialSignatoryFormFields = (index: number): FormFieldItem[] => [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Name),
      name: 'name',
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChangeOfficialSignatoryFormField(index, event),
      value: dealerData.officialSignatories[index].name,
      error: dealerFormFieldsErrors.includes(`officialSignatories[${index}].name`),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Email),
      name: 'email',
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChangeOfficialSignatoryFormField(index, event),
      value: dealerData.officialSignatories[index].email,
      error: dealerFormFieldsErrors.includes(`officialSignatories[${index}].email`),
    },
    ...(personalNumberCountries.includes(
      companiesData?.find(item => item.id === dealerData.companyId)?.country as Country,
    )
      ? [
          {
            fieldType: FormFieldType.INPUT,
            label: t(TranslationKey['Personal number']),
            name: 'personalNumber',
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => onChangeOfficialSignatoryFormField(index, event),
            value: dealerData.officialSignatories[index].personalNumber,
            error: dealerFormFieldsErrors.includes(`officialSignatories[${index}].personalNumber`),
          },
        ]
      : [
          {
            fieldType: FormFieldType.DATE_PICKER,
            label: t(TranslationKey['Date of birth']),
            name: 'dateOfBirth',
            onChange: (value: string) => onChangeOfficialSignatoryFormField(index, undefined, value, 'dateOfBirth'),
            value: dealerData.officialSignatories[index].dateOfBirth,
            error: dealerFormFieldsErrors.includes(`officialSignatories[${index}].dateOfBirth`),
          },
        ]),
  ]

  /**
   * A custom validation function for the Dealer form fields
   * @returns If any Dealer form field is invalid
   */
  const validateData = (): boolean => {
    const notRequiredFields: string[] = []

    const notRequiredSignatoryField = personalNumberCountries.includes(
      companiesData?.find(company => company.id === dealerData.companyId)?.country as Country,
    )
      ? 'dateOfBirth'
      : 'personalNumber'

    const invalidValues = validateFields(dealerData)
      .filter(item => !notRequiredFields.includes(item))
      .filter(item => !item.includes(notRequiredSignatoryField))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setDealerFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * A function handling the Add Official signatory button click
   */
  const onAddOfficialSignatory = () => {
    setDealerData(prev => ({
      ...prev,
      officialSignatories: [
        ...prev.officialSignatories,
        {
          name: '',
          dateOfBirth: '',
          email: '',
          personalNumber: '',
        },
      ],
    }))
  }

  /**
   * A function handling the Remove Official signatory button click
   * @param index Official signatory section index
   */
  const onRemoveOfficialSignatory = (index: number) => {
    setDealerData(prev => ({
      ...prev,
      officialSignatories: prev.officialSignatories.filter((_, i) => i !== index),
    }))
    setDealerFormFieldsErrors(prev => prev.filter(item => item.includes(`officialSignatory[${index}]`)))
  }

  /**
   * A function handling the Save button click
   * If form is valid, will update or save a new Dealer
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.CONTRACT_TYPE_VIEW}/${id}`)
    }

    if (id) {
      updateDealerMutation(
        {
          id,
          body: dealerData as DealerRequest,
        },
        {
          onSuccess: () => {
            clearAndExit(id)
          },
        },
      )
    } else {
      createDealerMutation(dealerData as DealerRequest, {onSuccess: ({id}) => clearAndExit(id)})
    }
  }

  /**
   * A function handling Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  return {
    onDiscard,
    onSubmit,
    onAddOfficialSignatory,
    dealerOfficialSignatoryFormFields,
    onRemoveOfficialSignatory,
    dealerData,
    t,
    dealerContactFormFields,
    dealerFormFields,
  }
}
